import { TFunction } from 'i18next';
import { WidgetType } from './shared/types';

export type UsedOnBoard = 'Dashboard' | 'Customer' | 'Concept';

export const widgetBoardMap: Record<WidgetType, UsedOnBoard[]> = {
  [WidgetType.Brands]: ['Dashboard'],
  [WidgetType.Articles]: [],
  [WidgetType.ArticlesCount]: [],
  [WidgetType.ProductApprovalsCount]: ['Dashboard', 'Customer'],
  [WidgetType.TrackSamples]: [],
  [WidgetType.TrackInquiries]: ['Dashboard', 'Customer', 'Concept'],
  [WidgetType.MyActiveShipments]: ['Dashboard'],
  [WidgetType.ProductApprovalLeadTime]: ['Customer'],
  [WidgetType.ProductApprovalRatio]: ['Customer'],
  [WidgetType.ArticleProgress]: ['Customer'],
  [WidgetType.PaLeadTimeDistribution]: ['Customer'],
  [WidgetType.SupplierCoverageHeatmap]: ['Dashboard'],
  [WidgetType.Spacer]: [],
  [WidgetType.PaRejectionReasons]: ['Dashboard', 'Customer'],
  [WidgetType.TrackOffers]: [],
};

export enum WidgetCategory {
  General = 1,
  Articles = 2,
  ProductApprovals = 3,
  Samples = 4,
  Transport = 5,
}

export interface BaseWidgetProps {
  widgetId: string;
  color: string | null;
  customTitle?: string;
}

export interface WidgetConfig {
  label: string;
  description: string;
  type: WidgetType;
  isBeta?: boolean;
  hidden?: boolean;
  noCustomization?: boolean;
  maximum: number | null;
  category: WidgetCategory;
}

type WidgetConfigs = { readonly [key in WidgetType]: WidgetConfig };

export const getWidgetConfigs = (t: TFunction): WidgetConfigs => {
  return {
    [WidgetType.Brands]: {
      label: t('widgets.brands.yourBrands'),
      description: t('widgets.brands.desc'),
      type: WidgetType.Brands,
      maximum: 1,
      category: WidgetCategory.General,
    },
    [WidgetType.Articles]: {
      label: t('widgets.articles.articleSearch'),
      description: t('widgets.articles.desc'),
      type: WidgetType.Articles,
      maximum: null,
      category: WidgetCategory.Articles,
    },
    [WidgetType.ArticlesCount]: {
      label: t('widgets.articlesCount.title'),
      description: t('widgets.articlesCount.desc'),
      type: WidgetType.ArticlesCount,
      maximum: null,
      category: WidgetCategory.Articles,
    },
    [WidgetType.ProductApprovalsCount]: {
      label: t('widgets.productApprovalCount.title'),
      description: t('widgets.productApprovalCount.desc'),
      type: WidgetType.ProductApprovalsCount,
      maximum: null,
      category: WidgetCategory.ProductApprovals,
    },
    [WidgetType.TrackSamples]: {
      label: 'Tracked samples',
      description: 'View all samples that you are currently tracking',
      type: WidgetType.TrackSamples,
      maximum: null,
      category: WidgetCategory.Samples,
    },
    [WidgetType.TrackInquiries]: {
      label: 'Tracked inquiries',
      description: 'View all inquiries that you are currently tracking',
      type: WidgetType.TrackInquiries,
      maximum: null,
      category: WidgetCategory.Articles,
      isBeta: true,
    },
    [WidgetType.MyActiveShipments]: {
      label: 'My shipments',
      description: 'View all incoming shipments and samples to you',
      type: WidgetType.MyActiveShipments,
      maximum: 1,
      category: WidgetCategory.Transport,
    },
    [WidgetType.ProductApprovalLeadTime]: {
      label: t('widgets.paLeadTime.title'),
      description: t('widgets.paLeadTime.desc'),
      type: WidgetType.ProductApprovalLeadTime,
      maximum: null,
      category: WidgetCategory.ProductApprovals,
    },
    [WidgetType.ProductApprovalRatio]: {
      label: t('widgets.paRatio.title'),
      description: t('widgets.paRatio.desc'),
      type: WidgetType.ProductApprovalRatio,
      maximum: null,
      category: WidgetCategory.ProductApprovals,
    },
    [WidgetType.ArticleProgress]: {
      label: t('widgets.articleProgress.title'),
      description: t('widgets.articleProgress.desc'),
      type: WidgetType.ArticleProgress,
      maximum: null,
      category: WidgetCategory.Articles,
    },
    [WidgetType.PaLeadTimeDistribution]: {
      label: t('widgets.paLeadTimeDistribution.title'),
      description: t('widgets.paLeadTimeDistribution.desc'),
      type: WidgetType.PaLeadTimeDistribution,
      maximum: null,
      category: WidgetCategory.ProductApprovals,
    },
    [WidgetType.SupplierCoverageHeatmap]: {
      label: 'Supplier Coverage Heatmap',
      description: 'View supplier coverage across branches and product groups',
      type: WidgetType.SupplierCoverageHeatmap,
      maximum: null,
      hidden: true,
      category: WidgetCategory.General,
    },
    [WidgetType.PaRejectionReasons]: {
      label: t('widgets.paRejectionReasons.title'),
      description: t('widgets.paRejectionReasons.desc'),
      type: WidgetType.PaRejectionReasons,
      maximum: null,
      category: WidgetCategory.ProductApprovals,
    },
    [WidgetType.Spacer]: {
      label: t('widgets.spacer.title'),
      description: t('widgets.spacer.desc'),
      type: WidgetType.Spacer,
      maximum: null,
      category: WidgetCategory.General,
    },
    [WidgetType.TrackOffers]: {
      label: 'Tracked offers',
      description: 'View all offers that you are currently tracking',
      type: WidgetType.TrackOffers,
      maximum: null,
      category: WidgetCategory.Articles,
      isBeta: true,
    },
  };
};
